import React, { useState, useEffect, useRef } from 'react';
import { FaRobot, FaTimes, FaMicrophone, FaPaperPlane, FaKey } from 'react-icons/fa';
import './AiChatWidget.css';
import { processAiQuery } from './AiService';

const renderFormattedText = (text) => {
  if (!text) return null;
  const lines = text.split('\n');

  return lines.map((line, lineIdx) => {
    const parts = line.split(/(\*\*.*?\*\*|`.*?`)/g);

    return (
      <div key={lineIdx} style={{ minHeight: '1.2em', marginBottom: '2px' }}>
        {parts.map((part, pIdx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
          }
          if (part.startsWith('`') && part.endsWith('`')) {
            return (
              <code
                key={pIdx}
                style={{
                  background: '#f1f5f9',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  color: '#0284c7',
                  border: '1px solid #e2e8f0',
                  fontSize: '12px'
                }}
              >
                {part.slice(1, -1)}
              </code>
            );
          }
          return part;
        })}
      </div>
    );
  });
};

const AiChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputQuery, setInputQuery] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: `👋 Hello! I am **VT AI Assistant**.\nAsk me **anything in plain English** (patient search, admissions, collections, clinical advice, hospital procedure)!`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);

  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  const handleConfigureApiKey = () => {
    const existingKey = localStorage.getItem('VT_GEMINI_API_KEY') || '';
    const key = prompt('Enter your Google Gemini API Key for direct AI LLM generation:', existingKey);
    if (key !== null) {
      localStorage.setItem('VT_GEMINI_API_KEY', key.trim());
      alert(key.trim() ? 'Gemini API Key saved successfully!' : 'Gemini API Key cleared.');
    }
  };

  const handleVoiceListen = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert('Voice dictation is supported natively in Google Chrome and Microsoft Edge browsers.');
      return;
    }

    if (isRecording) {
      setIsRecording(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputQuery(transcript);
        setIsRecording(false);
      };

      recognition.onerror = (err) => {
        console.error('Speech recognition error:', err);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
    } catch (e) {
      console.error(e);
      setIsRecording(false);
    }
  };

  const handleSendMessage = async (textToSend) => {
    const query = textToSend || inputQuery;
    if (!query || !query.trim()) return;

    const userMsg = {
      sender: 'user',
      text: query,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputQuery('');
    setLoading(true);

    try {
      const aiResponse = await processAiQuery(query, lastContext);
      if (aiResponse.context) {
        setLastContext(aiResponse.context);
      }
      const aiMsg = {
        sender: 'ai',
        text: aiResponse.text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      const errorMsg = {
        sender: 'ai',
        text: '⚠️ Something went wrong connecting to live hospital data.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Action Button Trigger */}
      {!isOpen && (
        <button
          className="ai-chat-fab-circle"
          onClick={() => setIsOpen(true)}
          title="Open VT Assistant"
        >
          <FaRobot className="ai-fab-icon-glow" />
          <span className="ai-fab-pulse-ring" />
          <span className="ai-fab-text">VT AI</span>
        </button>
      )}

      {/* Floating Chat Drawer Window */}
      {isOpen && (
        <div className="ai-chat-drawer">
          <div className="ai-chat-header">
            <div className="ai-header-info">
              <div className="ai-avatar-circle">🤖</div>
              <div>
                <h4 className="ai-header-title">VT Assistant</h4>
                <div className="ai-header-status">
                  <span className="ai-status-dot" /> Online & Connected
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className="ai-key-btn"
                onClick={handleConfigureApiKey}
                title="Configure Gemini API Key"
              >
                <FaKey />
                <span>API Key</span>
              </button>
              <button
                className="ai-close-btn"
                onClick={() => setIsOpen(false)}
                title="Close AI Assistant"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Chat Feed */}
          <div className="ai-chat-feed" ref={feedRef}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-bubble ${msg.sender}`}>
                <div>{renderFormattedText(msg.text)}</div>
                <div className="chat-time">{msg.time}</div>
              </div>
            ))}

            {loading && (
              <div className="chat-bubble ai">
                <span>🤖 Processing your request...</span>
              </div>
            )}

            {/* Quick Suggestion Chips */}
            <div className="ai-chips-wrapper">
              <button
                className="ai-chip-btn"
                onClick={() => handleSendMessage("calculate total due")}
              >
                💰 Total Dues
              </button>
              <button
                className="ai-chip-btn"
                onClick={() => handleSendMessage("highest due patient")}
              >
                🏆 Top Due Patients
              </button>
              <button
                className="ai-chip-btn"
                onClick={() => handleSendMessage("How much collection today?")}
              >
                💳 Revenue Today
              </button>
              <button
                className="ai-chip-btn"
                onClick={() => handleSendMessage("refunds today")}
              >
                💸 Refunds Today
              </button>
            </div>
          </div>

          {/* Chat Input Bar */}
          <div className="ai-chat-input-bar">
            <input
              type="text"
              className="ai-chat-input"
              placeholder={isRecording ? 'Listening... Speak now!' : 'Ask anything in plain English...'}
              value={inputQuery}
              onChange={(e) => setInputQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              className={`mic-btn ${isRecording ? 'recording' : ''}`}
              onClick={handleVoiceListen}
              title="Voice Dictation (Speak Prompt)"
            >
              <FaMicrophone />
            </button>
            <button
              className="send-btn"
              onClick={() => handleSendMessage()}
              title="Send Message"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AiChatWidget;
